import {createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useInteractions, useListNavigation} from '@floating-ui/react'
import {useSelectContext} from '../select_context'
import {useFloatingContext} from '../../../floating'
import {keyEventHandler} from './key_event_handler'

const FloatingSelectContext = createContext({
	getItemProps: x => x,
	activeIndex: null,
})
const {Provider} = FloatingSelectContext

const getDisabledItemIndices = (items, single, expandedGroups, groupSelectable) =>
	items.reduce((acc, item, idx) => {
		const {type, data, items: groupItems} = item
		const isItemDisabled = type === 'item' && data.disabled
		const isGroupDisabled = type === 'group' && (!groupSelectable || single || groupItems.every(({disabled}) => disabled))
		const isItemGroupCollapsed = item.group && !expandedGroups.has(item.group)

		if (isItemDisabled || isGroupDisabled || isItemGroupCollapsed) acc.push(idx)

		return acc
	}, [])

export const FloatingSelectProvider = ({children}) => {
	const {updateValue, items, selectedIndex, single, expandedGroups, groupProps} = useSelectContext()
	const {context, getFloatingProps} = useFloatingContext()
	const [activeIndex, setActiveIndex] = useState(selectedIndex)
	const [focusedNestedGroup, setFocusedNestedGroup] = useState(null)

	const listItems = useMemo(() => items.filter(item => item.type !== 'nested-item'), [items])

	// add a ref slot for each item, to ensure list navigation works in virtualized lists as well
	// since in that case not all items are mounted in the DOM and their nodes are not added to the listRef.
	const listRef = useRef(Array(listItems.length))
	// make sure to update the listRef when the items change, which happens when searching.
	useLayoutEffect(() => {
		listRef.current = Array(listItems.length)
	}, [listItems])

	useEffect(() => {
		setActiveIndex(selectedIndex)
		setFocusedNestedGroup(null)
	}, [listItems, selectedIndex])

	const disabledIndices = getDisabledItemIndices(listItems, single, expandedGroups, groupProps?.groupSelectable)

	const {getFloatingProps: getListFloatingProps, getItemProps} = useInteractions([
		useListNavigation(context, {
			listRef,
			onNavigate: setActiveIndex,
			activeIndex,
			loop: true,
			focusItemOnOpen: false,
			focusItemOnHover: false,
			virtual: true,
			disabledIndices,
		}),
	])

	const floatingProps = getFloatingProps(getListFloatingProps())

	// add keyboard navigation to the floating element
	const floatingLayoutProps = {
		...floatingProps,
		onKeyDown: keyEventHandler({activeIndex, listRef, floatingProps, updateValue, listItems, focusedNestedGroup, setFocusedNestedGroup}),
	}

	const providerValue = {
		activeIndex,
		listRef,
		floatingLayoutProps,
		getItemProps,
		focusedNestedGroup,
	}

	return <Provider value={providerValue}>{children}</Provider>
}

export const useFloatingSelectContext = () => useContext(FloatingSelectContext)
